import React, { useState, useRef, useEffect } from 'react';
import Switcher from './Switcher';

const NewFilter = ({
  ratingso,
  questions,
  onFilteredResults,
  sortOrder,
  onActiveKeysChange,
  sortKey,
  onSortOrderChange,
  resetSignal,
}) => {
  const [switchStates, setSwitchStates] = useState({});
  const [sortDirections, setSortDirections] = useState({});
  const [isUsingFilter, setIsUsingFilter] = useState(false);
  const [lastActiveQuestion, setLastActiveQuestion] = useState(null);

  const handleSwitchChange = (questionId, isOn) => {
    setSwitchStates((prev) => {
      const updatedStates = {
        ...prev,
        [questionId]: isOn,
      };
      setIsUsingFilter(Object.values(updatedStates).some((val) => val));
      return updatedStates;
    });

    if (isOn) {
      setLastActiveQuestion(questionId);
      setSortDirections((prev) => ({
        ...prev,
        [questionId]: prev[questionId] || 'desc',
      }));

      onSortOrderChange('desc', questionId);
    } else {
      setSortDirections((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });

      // Update last active question if the current one is turned off
      if (questionId === lastActiveQuestion) {
        const remainingActive = Object.keys(switchStates).filter(
          (key) => key !== questionId && switchStates[key],
        );
        setLastActiveQuestion(
          remainingActive[remainingActive.length - 1] || null,
        );
      }
    }
  };

  const handleSortChange = (questionId, direction) => {
    setSortDirections((prev) => ({
      ...prev,
      [questionId]: direction,
    }));

    onSortOrderChange(direction, questionId);
  };

  const prevActiveKeysRef = useRef([]);

  useEffect(() => {
    const activeKeys = questions
      .filter((q) => switchStates[q.questionId])
      .map((q) => q.searchKey)
      .sort();

    const prevActiveKeys = prevActiveKeysRef.current;

    const areEqual =
      activeKeys.length === prevActiveKeys.length &&
      activeKeys.every((key, index) => key === prevActiveKeys[index]);

    if (!areEqual) {
      prevActiveKeysRef.current = activeKeys;
      onActiveKeysChange?.(activeKeys);
    }
  }, [switchStates, questions, onActiveKeysChange]);

  useEffect(() => {
    // reset all internal state whenever resetSignal changes
    setSwitchStates({});
    setSortDirections({});
    setIsUsingFilter(false);
    setLastActiveQuestion(null);
  }, [resetSignal]);

  const clearFilter = () => {
    setSwitchStates({});
    setSortDirections({});
    setIsUsingFilter(false);
    setLastActiveQuestion(null);
  };

  const contentData = ratingso.reduce((acc, curr) => {
    curr.ratings.forEach(({ questionId, rating }) => {
      if (!acc[curr.contentId]) acc[curr.contentId] = { questions: {} };
      if (!acc[curr.contentId].questions[questionId]) {
        acc[curr.contentId].questions[questionId] = {
          totalRating: 0,
          count: 0,
        };
      }
      acc[curr.contentId].questions[questionId].totalRating += rating;
      acc[curr.contentId].questions[questionId].count += 1;
    });
    return acc;
  }, {});

  const filteredResults = Object.keys(contentData).filter((contentId) => {
    const data = contentData[contentId];
    if (!isUsingFilter) return true;

    return Object.entries(switchStates).every(([questionId, isOn]) => {
      if (!isOn) return true;
      const qData = data.questions[questionId];
      if (!qData) return true;

      const avg = qData.totalRating / qData.count;
      return avg >= 0.5 && avg <= 10.0;
    });
  });

  const getSortedResults = (results) => {
    return results.sort((a, b) => {
      const getAvg = (contentId, questionId) => {
        const q = contentData[contentId]?.questions?.[questionId];
        return q ? q.totalRating / q.count : 0;
      };

      if (lastActiveQuestion) {
        const dir = sortDirections[lastActiveQuestion] || 'desc';
        const avgA = getAvg(a, lastActiveQuestion);
        const avgB = getAvg(b, lastActiveQuestion);

        if (avgA !== avgB) {
          return dir === 'asc' ? avgA - avgB : avgB - avgA;
        }
      }

      // fallback to overall score if primary comparison is equal
      let scoreA = 0;
      let scoreB = 0;

      Object.entries(switchStates).forEach(([questionId, isOn]) => {
        if (isOn) {
          const avgA = getAvg(a, questionId);
          const avgB = getAvg(b, questionId);
          const direction = sortDirections[questionId] || 'desc';

          if (direction === 'asc') {
            scoreA -= avgA;
            scoreB -= avgB;
          } else {
            scoreA += avgA;
            scoreB += avgB;
          }
        }
      });

      return scoreB - scoreA;
    });
  };

  const sortedResults = getSortedResults(filteredResults);

  useEffect(() => {
    onFilteredResults(sortedResults, isUsingFilter);
  }, [
    switchStates,
    ratingso,
    isUsingFilter,
    sortDirections,
    lastActiveQuestion,
  ]);

  return (
    <div className="bg-gray-800/70 p-3 rounded-lg border border-gray-700">
      <h3 className="text-sm font-semibold text-secondary mb-3 tracking-wide">
        Sort By Reviews
      </h3>

      <div className="space-y-2">
        {questions.map((item) => (
          <div
            key={item.questionId}
            className="flex flex-col gap-2 bg-gray-900/80 p-2.5 rounded-md border border-gray-700 hover:border-secondary/40 transition-colors"
          >
            {/* Row: Label + Switch */}
            <div className="flex justify-between items-center">
              <span className="text-gray-200 text-sm font-medium truncate">
                {item.searchKey}
              </span>
              <Switcher
                isOn={switchStates[item.questionId] || false}
                onToggle={(isOn) => handleSwitchChange(item.questionId, isOn)}
              />
            </div>

            {/* Sort Controls (only show when active) */}
            {switchStates[item.questionId] && (
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => handleSortChange(item.questionId, 'desc')}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    sortDirections[item.questionId] === 'desc'
                      ? 'bg-secondary text-primary'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Top ↓
                </button>
                <button
                  onClick={() => handleSortChange(item.questionId, 'asc')}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    sortDirections[item.questionId] === 'asc'
                      ? 'bg-secondary text-primary'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Bottom ↑
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Clear Button */}
      <div className="mt-4 flex justify-center">
        <button
          onClick={clearFilter}
          className="w-full bg-secondary hover:bg-secondary/80 text-primary text-sm font-semibold py-2 rounded-lg transition-all"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default NewFilter;
